import { Component, OnInit, AfterViewChecked, ChangeDetectorRef, ViewEncapsulation } from '@angular/core';
import { Project } from '../shared/models/project';
import { Employee} from '../shared/models/employee';
import { PpsUtilityService } from '../services/pps-utility.service';
import { IEntity } from '../shared/models/entity';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { bankGuaranteeRequiredValidator } from './validators/bank-guarantee-required.validator';
import { AccessPermission } from '../shared/models/access-permission';
import { poRequiredValidator } from './validators/po-required.validator';
import { Company, CompanyRepresentative } from '../shared/models/company';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ProjectService } from '../services/project.service';
import { SubSink } from 'subsink';
import { IProjectComment } from '../shared/models/project-comment';
import { format, parseISO } from 'date-fns';
import { ToastService } from '../shared/toast/toast.service';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { IDocument } from '../shared/models/document';
import { DocumentService } from '../services/document-service';
import { ConfirmationService } from 'primeng/api';
import { EmployeeBase } from '../shared/models/employee-base';
import { UserRoleService } from '../services/user-role.service';
import { User } from '../shared/models/user';

interface IAttachment{
  fileName: string;
  fileSize: string;
}

@Component({
  selector: 'pps-project-form',
  templateUrl: './project-form.component.html',
  styleUrls: ['./project-form.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class ProjectFormComponent implements OnInit, AfterViewChecked{
  smallScreen: boolean = false;
  private subs = new SubSink();
  
  projectColumnAccessPermission: any = {};

  project: Project = new Project();
  selectedProjectStatus:any = null;

  attachment: IAttachment = { fileName: "", fileSize: "0"};
  projectAttachment: File = null;

  projectComments: IProjectComment[] = [];
  loadingComment = false;
  commentInEditing: IProjectComment = null;
  defaultQuotingDeadline = new Date();
  sourceColumnId = ""; // Indicates event source to open this form
  activeTabIndex = 0;

  clients: Company[] = [];
  representatives: any = {};

  jobTypes: IEntity[] = [];
  materialProcurementStatuses: IEntity[] = [];
  selectedMaterialStatus: any = null;
  projectStatuses: IEntity[] = [];
  
  clientEngineers: CompanyRepresentative[] = [];
  responsibleEngineers: Employee[] = [];
  installationProgresses: IEntity[] = [];

  projectForm: UntypedFormGroup;
  submitted = false;

  currentUser: User;

  constructor(
    private dialogRef: DynamicDialogRef,
    private dlgConfig: DynamicDialogConfig,
    private projectService: ProjectService,
    private documentService: DocumentService,
    public util: PpsUtilityService,
    private formBuilder: UntypedFormBuilder,
    private _toast: ToastService,
    private _userRoleService: UserRoleService,
    private changeDetectorRef:ChangeDetectorRef,
    private _confirmationService: ConfirmationService,
    private breakpointObserver: BreakpointObserver) {
      this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
        .subscribe((state: BreakpointState) => {
          this.smallScreen = !state.matches;
        });
    this.setInitialValues();
  }

  ngOnInit(): void {
    this.defaultQuotingDeadline.setHours(17, 0, 0);
    let canAccessComment = this.hasAccess("ProjectComment");
    let canAccessAttachment = this.hasAccess("ProjectAttachment");
    let hasBudgetTab = this.showBudgetTab();

    if (this.clickedOnBudgetColumn()){
      this.activeTabIndex = 3;
    }else{
      this.activeTabIndex = this.sourceColumnId === "projectComment" ? 3 : this.sourceColumnId === "hasAttachment" ? canAccessComment ? 4 : 3 : 0;
      if (hasBudgetTab && this.activeTabIndex > 0){
        this.activeTabIndex ++;
      }
    }

    if (canAccessComment){
      this.loadProjectComments();
    }

    if (canAccessAttachment){
      this.loadProjectAttachments();
    }

    this.getUploadUrl(this.PpsProject.id);
  }

  ngOnDestroy():void{
    this.subs.unsubscribe();
  }  

  ngAfterViewChecked()
  {
    this.changeDetectorRef.detectChanges();
  }

  onMaterialStatusChanged(evt){
    if (evt){
      this.selectedMaterialStatus = evt.value;
    }
  }

  onClientChange(evt){
    if (evt){
      let client = evt.value;
      this.clientEngineers = !!client ? this.representatives[client.id] : [];
      if (this.project.client?.id !== this.projectForm.controls["client"].value?.id){
        this.projectForm.controls["clientEngineers"].reset();
      }
    }
  }

  onProjectStatusChanged(evt){
    if (!evt || !evt.value){
      return;
    }

    this.selectedProjectStatus = evt.value;

    this.f.poAmount.setValue(this.poReceived ? this.project.poAmount : 0);
    this.f.poNumber.setValue(this.poReceived ? this.project.poNumber : "");

    if (this.selectedProjectStatus.name === "RFQ" ){
      this.f.quotingDeadline.setValidators(Validators.required);
    }else{
      this.f.quotingDeadline.clearValidators();
    }

    this.f.quotingDeadline.updateValueAndValidity();
  }

  onCompletionPercentageChanged(event){
    let poAmount = this.f.poAmount.value;
    let per = this.f.completionPercentage.value;
    if (!isNaN(poAmount) && !isNaN(per)){
      let estimatedCompletionValue = (poAmount * per / 100.0);
      this.project.estimatedCompletionValue = estimatedCompletionValue;
      this.f.estimatedCompletionValue.setValue(estimatedCompletionValue);
    }
  }

  onContractNumberChanged($event){
    // Remove the logic per Eric's request:
    // Some contract is Rentention Money, which does not have bank guarantee. 
    // So we should not force user to enter bank guarantee if contract number is entered.
    
    // if (!this.f.contractNumber.value){
    //   this.f.bankGuaranteeAmount.setErrors(null);
    //   this.f.bankGuaranteeExpirationDate.setErrors(null);
    // }
  }  

  save(){
    this.submitted = true;

    if (this.projectForm.invalid){
      return;
    }

    if (this.PpsProject.comment?.trim()){
      this.postComment(null, () => this.dialogRef.close(this.PpsProject));
    }else{
      this.dialogRef.close(this.PpsProject);
    }
  }

  cancel(){
    this.dialogRef.close();
  }

  reset(){
    this.submitted = false;
    this.projectForm.reset();
  }

  get f(){
    return this.projectForm.controls;
  }

  private setInitialValues(){
    this.Companies = this.dlgConfig.data.companies;
    this.Representatives = this.dlgConfig.data.representatives;
    this.PpsEntites = this.dlgConfig.data.ppsEntites;
    this.PpsProject = this.dlgConfig.data.ppsProject;
    this.PpsProjectColumnAccessPermission = this.dlgConfig.data.ppsProjectColumnAccessPermission;
    this.sourceColumnId = this.dlgConfig.data.sourceColumnId;
    this.currentUser = this.dlgConfig.data.currentUser;
  }

  set PpsEntites(projectEntities: any){
    this.jobTypes = projectEntities.jobTypes;
    this.materialProcurementStatuses = projectEntities.materialProcurementStatuses;
    this.projectStatuses = projectEntities.projectStatuses;
    this.installationProgresses = projectEntities.installationProgresses;
    this.responsibleEngineers = projectEntities.responsibleEngineers;
  }

  set PpsProject(prj: Project){
    this.project = prj;

    this.getProjectFormGroup(this.project);
    this.onProjectStatusChanged({value:this.project.projectStatus});
    this.onMaterialStatusChanged({value:this.project.materialProcurementStatus});
    this.onClientChange({value:this.project.client});
  }

  set PpsProjectColumnAccessPermission(projectColumnAccessPermission){
    this.projectColumnAccessPermission = projectColumnAccessPermission;
    for (let formControlName in this.projectForm.controls){
      let colName = formControlName.charAt(0).toUpperCase() + formControlName.slice(1);

      if (this.projectColumnAccessPermission[colName] &&
        this.projectColumnAccessPermission[colName].accessPermission === AccessPermission.ReadAccess){
        this.projectForm.controls[formControlName].disable({onlySelf: true});
      }
    }

    if (this.projectColumnAccessPermission["ProjectComment"] &&
      this.projectColumnAccessPermission["ProjectComment"].accessPermission === AccessPermission.ReadAccess){
      this.projectForm.controls["comment"].disable({onlySelf: true});
    }    
  }

  set Companies(companies){
    this.clients = companies;
  }

  set Representatives(reps){
    this.representatives = reps;
  }

  get PpsProject(){
    for (let formControlName in this.projectForm.controls){
      this.project[formControlName] = this.projectForm.controls[formControlName].value;
    }

    return this.project;
  }

  private getProjectFormGroup(project: Project){
    this.projectForm = this.formBuilder.group({
      "projectNo": [project.projectNo, [Validators.required, Validators.minLength(7)]],
      "projectStatus": [project.projectStatus, Validators.required],
      "poNumber": [project.poNumber],
      "poAmount": [project.poAmount],
      "description": [project.description, Validators.required],
      "jobType": [project.jobType, Validators.required],
      "responsibleEngineer": [project.responsibleEngineer, Validators.required],
      "client": [project.client],
      "clientEngineers": [project.clientEngineers],
      "quotingDeadline": [project.quotingDeadline],
      "materialProcurementStatus": [project.materialProcurementStatus],
      "materialEstimateDeliveryDate": [project.materialEstimateDeliveryDate],
      "materialActualArrivalDate": [project.materialActualArrivalDate],
      "materialStatusMemo": [project.materialStatusMemo],
      "installationProgress": [project.installationProgress],
      "workOrder": [project.workOrder],
      "targetStartDate": [project.targetStartDate],
      "targetCompletionDate": [project.targetCompletionDate],
      "completionPercentage": [project.completionPercentage, [Validators.min(0), Validators.max(100)]],
      "budgetHour": [project.budgetHour, [Validators.min(0), Validators.max(100000)]],
      "plannedHour": [project.plannedHour, [Validators.min(0), Validators.max(100000)]],
      "actualHour": [project.actualHour],
      "contractNumber": [project.contractNumber],
      "bankGuaranteeAmount": [project.bankGuaranteeAmount],
      "bankGuaranteeExpirationDate": [project.bankGuaranteeExpirationDate],
      "earnedManHours": [project.earnedManHours],
      "estimatedCompletionValue": [project.estimatedCompletionValue],
      "comment": [project.comment],
      "commentInEditing": [project.comment],

      "directLabor": [project.directLabor],
      "plantAndEquipment": [project.plantAndEquipment],
      "materialsAndConsumbles": [project.materialsAndConsumbles],
      "management": [project.management],
      "thirdParty": [project.thirdParty],
    }, 
    {
      validator: Validators.compose([
                    bankGuaranteeRequiredValidator,
                    poRequiredValidator])
    });

    this.f.completionPercentage.valueChanges.subscribe(value=>{
      this.onCompletionPercentageChanged(null);
    });
  }

  errorInInfoFields(): boolean{
    return this.submitted && (
      !!this.f.projectNo.errors ||
      !!this.f.projectStatus.errors ||
      !!this.f.poNumber.errors ||
      !!this.f.poAmount.errors ||
      !!this.f.description.errors ||
      !!this.f.jobType.errors ||
      !!this.f.responsibleEngineer.errors ||
      !!this.f.clientEngineers.errors ||
      !!this.f.quotingDeadline.errors);
  }

  errorInExecutionFields(): boolean{
    return this.submitted && (
      !!this.f.installationProgress.errors ||
      !!this.f.workOrder.errors ||
      !!this.f.targetStartDate.errors ||
      !!this.f.targetCompletionDate.errors ||
      !!this.f.budgetHour.errors ||
      !!this.f.plannedHour.errors ||
      !!this.f.completionPercentage.errors ||
      !!this.f.contractNumber.errors ||
      !!this.f.bankGuaranteeAmount.errors ||
      !!this.f.bankGuaranteeExpirationDate.errors ||
      !!this.f.estimatedCompletionValue.errors);
  }

  errorInMaterialFields(): boolean{
    return this.submitted && (
      !!this.f.materialEstimateDeliveryDate.errors ||
      !!this.f.materialActualArrivalDate.errors);
  }

  compareFn(a, b) {
    return a && b && +a.id === +b.id;
  }

  isNewProject(){
    return this.project === null || this.project.id === 0;
  }

  hasAccess(colName){
    return this.projectColumnAccessPermission[colName] && (
      this.projectColumnAccessPermission[colName].accessPermission === AccessPermission.ReadAccess ||
      this.projectColumnAccessPermission[colName].accessPermission === AccessPermission.WriteAccess ||
      this.projectColumnAccessPermission[colName].accessPermission === AccessPermission.ExecuteAccess
    );
  }

  showBudgetTab(){
    const budgetColumns = [
      "DirectLabor",
      "PlantAndEquipment",
      "MaterialsAndConsumbles",
      "Management",
      "ThirdParty"
    ];

    for(let i = 0; i < budgetColumns.length; i ++){
      if (this.projectColumnAccessPermission[budgetColumns[i]] && 
        this.projectColumnAccessPermission[budgetColumns[i]].accessPermission !== AccessPermission.NoAccess){
        return true;
      }
    }

    return false;
  }

  readonlyAccess(colName){
    return this.projectColumnAccessPermission[colName] &&
      this.projectColumnAccessPermission[colName].accessPermission === AccessPermission.ReadAccess;
  }

  get jobLostOrCancelled(): boolean{
    return this.selectedProjectStatus && (this.selectedProjectStatus.name === "Job Lost" || this.selectedProjectStatus.name === "Job Cancelled");
  }

  get poReceived(): boolean{
    return this.selectedProjectStatus && this.selectedProjectStatus.name === "PO Received" && this.selectedProjectStatus.entityCategory === 2;
  }
  
  get needMaterialEstimateDeliveryDate(): boolean{
    return (!!this.selectedMaterialStatus && 
      (this.selectedMaterialStatus.name === "PO Raised" || this.selectedMaterialStatus.name === "Under Delivery")  &&
      this.selectedMaterialStatus.entityCategory === 8);
  }

  get needMaterialActualArrivalDate(): boolean{
    return (!!this.selectedMaterialStatus && 
      this.selectedMaterialStatus.name === "Material Arrived"  &&
      this.selectedMaterialStatus.entityCategory === 8);
  }

  // Comment tab
  showDisplayPostButton(){
    return this.projectColumnAccessPermission["ProjectComment"] &&
      this.projectColumnAccessPermission["ProjectComment"].accessPermission === AccessPermission.WriteAccess;
  }
  
  isExistingProject(){
    return this.PpsProject.id > 0;
  }

  loadProjectComments(){
    this.projectComments = [];
    this.loadingComment = true;
    this.commentInEditing =  null;
    this.subs.sink = this.projectService.getProjectComments(this.PpsProject.id)
      .subscribe(res => {
        if (res.succeed){
          this.projectComments = res.result;
        }
      })
      .add(()=>this.loadingComment = false);
  }

  postComment(comment: IProjectComment, fnAfter = null){
    var projectComment: IProjectComment = comment === null ? {
      commentId: 0,
      commentDate: new Date(),
      comment: this.PpsProject.comment
    } : comment;

    this.commentInEditing =  null;
    if (projectComment.comment?.trim()){
      this.subs.sink = this.projectService.addProjectComment(this.PpsProject.id, projectComment)
      .subscribe(res => {
        if (res.succeed){
          this.f.comment.setValue("");

          this.loadProjectComments();
        }else{
          this._toast.failed(`Failed to post comment: ${res.errors.join(";")}`, 5000, "formToast");
        }
      })
      .add(() => {
        if (fnAfter){
          fnAfter();
        }
      });
    }else{
      if (!projectComment.comment?.trim()){
        this._toast.failed("You cannot post empty comment", 5000, "formToast");
      }
    }
  }

  canEditComment(addBy: string){
    return addBy === this.currentUser.zoesenUserId;
  }

  startEditComment(comment: IProjectComment){
    this.commentInEditing = comment;
    this.f.commentInEditing.setValue(comment.comment);
  }
  cancelCommentEditing(){
    this.commentInEditing = null;
    this.f.commentInEditing.setValue("");
  }

  getDate(dateValue:Date): string{
    try{
      return format(new Date(dateValue), "dd/MM/yyyy hh:mm:ss a");
    }
    catch(e){
      console.log(dateValue);
    }
  }

  updatingComment = false;
  updateComment(){
    this.updatingComment = true;
    this.commentInEditing.comment = this.f.commentInEditing.value;
    this.commentInEditing.commentDate = new Date();
    console.log(this.commentInEditing);
    this.postComment(this.commentInEditing, () => this.updatingComment = false);
  }

  // project attachment
  documents: IDocument[] = [];
  loadingAttachments = false;
  loadProjectAttachments(){
    this.projectComments = [];
    this.loadingAttachments = true;
    this.subs.sink = this.projectService.getProjectAttachments(this.PpsProject.id)
      .subscribe(res => {
        if (res.succeed){
          this.documents = res.result;
          console.log(this.documents);
        }
      })
      .add(()=>this.loadingAttachments = false);
  }
  uploadUrl: string;
  getUploadUrl(projectId: number){
    this.uploadUrl= this.projectService.getAttachmentUploadUrl(projectId);
  }

  beforeProjectAttachmentUpload(event){
    console.log(event);
  }

  onProjectAttachmentUpload(event){
    for(let file of event.files) {
      //this.uploadedFiles.push(file);
      console.log(file.name + " " + file.size);
    }

    this.loadProjectAttachments();
  }

  dragOverDropZone = false;
  mouseOverDropArea(evt){
    this.dragOverDropZone = true;
  }

  mouseOutDropArea(evt){
    this.dragOverDropZone = false;
  }

  getUploadDate(uploadDate){
    return format(uploadDate, "dd/MM/dddd HH:mm");
  }

  downloadAttachment(docUrl: string, filename: string){
    this.documentService.download(docUrl, 2, filename, null);
  }

  deleteAttachment(filename: string, originalFilename: string){
    this._confirmationService.confirm({
      header: `Delete Attachment`,
      message: `Please confirm that you want to delete ${originalFilename}?`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      key: "deleteAttachment",
      accept: () => {
        this.subs.sink =  this.projectService.deleteProjectAttachment(this.PpsProject.id, filename)
        .subscribe(res=>{
          if (res.succeed){
            this.loadProjectAttachments();
          }else{
            this._toast.failed("Failed to delete the attachment");
          }
        });
      }
    });    
  }

  private clickedOnBudgetColumn(){
    return this.sourceColumnId === "directLabor" ||
    this.sourceColumnId === "plantAndEquipment" ||
    this.sourceColumnId === "materialsAndConsumbles" ||
    this.sourceColumnId === "management" ||
    this.sourceColumnId === "thirdParty";
  }
}
